import React from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";
import { useIsLoggedIn } from "Flocknote/stateHooks";

import FnrIcon, { FnrIconWrapper } from "UI/FnrIcon";

import { addHttpToUrl } from "Utilities/addHttpToUrl";

import { useGetHomeSettings } from "../../API/queries";
import { useGetBackgroundColorData } from "../../API/queryHooks";
import XLogo from "../../shared/XLogo";

const FooterSection = () => {
  const { data: homeSettings } = useGetHomeSettings();
  const [{ address, networkWebsite }] = useNetworkMetadata();
  const { textColor } = useGetBackgroundColorData();
  const isLoggedIn = useIsLoggedIn();
  const showRecaptchaNotice = homeSettings?.forceRecaptcha && !isLoggedIn;

  const networkWebsiteSafe = addHttpToUrl(networkWebsite);
  const darkenFooter = homeSettings?.aboutUs;
  const textColorToUse = darkenFooter ? "white" : textColor;

  return (
    <FooterContainer $darkenFooter={darkenFooter} $textColor={textColorToUse}>
      <CenteredContent>
        <ContactInfo>
          {networkWebsite && (
            <ContactItem>
              <SharedIconWrapper>
                <FnrIcon far fa-desktop-alt />
              </SharedIconWrapper>
              <ContactText>
                <WebsiteLink
                  href={networkWebsiteSafe}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {networkWebsiteSafe}
                </WebsiteLink>
              </ContactText>
            </ContactItem>
          )}
          {(address?.street ||
            address?.city ||
            address?.state ||
            address?.zip) && (
            <ContactItem>
              <SharedIconWrapper style={{ transform: "translateY(-1px)" }}>
                <FnrIcon far fa-map-marker-alt />
              </SharedIconWrapper>
              <ContactText>
                {address?.street ? (
                  <div>{address.street}</div>
                ) : (
                  <NoStreetSpacer />
                )}
                {(address?.city || address?.state || address?.zip) && (
                  <div
                    style={{ fontSize: address?.street ? "0.9rem" : "1rem" }}
                  >
                    {address?.city}, {address?.state} {address?.zip}
                  </div>
                )}
              </ContactText>
            </ContactItem>
          )}
        </ContactInfo>

        <RightContent>
          <SocialIcons>
            {homeSettings?.socials?.Facebook?.url && (
              <SocialIcon
                href={homeSettings.socials.Facebook.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Facebook"
              >
                <SocialIconWrapper>
                  <FnrIcon fab fa-facebook-square />
                </SocialIconWrapper>
              </SocialIcon>
            )}
            {homeSettings?.socials?.Instagram?.url && (
              <SocialIcon
                href={homeSettings.socials.Instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Instagram"
              >
                <SocialIconWrapper>
                  <FnrIcon fab fa-instagram />
                </SocialIconWrapper>
              </SocialIcon>
            )}
            {homeSettings?.socials?.Twitter?.url && (
              <SocialIcon
                href={homeSettings.socials.Twitter.url}
                target="_blank"
                rel="noopener noreferrer"
                title="X (Twitter)"
              >
                <XLogo color={textColorToUse} size={24} />
              </SocialIcon>
            )}
            {homeSettings?.socials?.Youtube?.url && (
              <SocialIcon
                href={homeSettings.socials.Youtube.url}
                target="_blank"
                rel="noopener noreferrer"
                title="YouTube"
              >
                <SocialIconWrapper>
                  <FnrIcon fab fa-youtube />
                </SocialIconWrapper>
              </SocialIcon>
            )}
          </SocialIcons>
          <PoweredBy>
            <FnrIcon
              icon="uno"
              style={{
                fontSize: "1.1rem",
                transform: "translateY(-2px)",
              }}
            />
            <PoweredByText>
              Powered by{" "}
              <WebsiteLink
                href="https://flocknote.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Flocknote
              </WebsiteLink>
            </PoweredByText>
          </PoweredBy>
          {showRecaptchaNotice && (
            <RecaptchaNotice>
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
              apply.{" "}
            </RecaptchaNotice>
          )}
        </RightContent>
      </CenteredContent>
    </FooterContainer>
  );
};

const SharedIconWrapper = styled(FnrIconWrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  margin-top: 4px;
  width: 20px;
  height: 20px;
`;

const SocialIconWrapper = styled(FnrIconWrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-size: 1.8rem;
`;

const WebsiteLink = styled.a`
  color: inherit;
  text-decoration: none;
  transition: text-decoration 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${({ $darkenFooter }) =>
    $darkenFooter ? "rgba(0, 0, 0, 0.3)" : "transparent"};
  color: ${({ $textColor }) => $textColor};
  padding-bottom: 230px;
`;

const CenteredContent = styled.div`
  padding: 2rem 4rem;
  display: flex;
  justify-content: space-between;
  /* align-items: flex-end; */
  width: 100%;
  max-width: 1200px;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem 2rem;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 4rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const ContactInfo = styled.div`
  color: inherit;
  font-size: 1.1rem;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
  gap: 1.3rem;

  @media (max-width: 768px) {
    text-align: center;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ContactText = styled.div`
  line-height: 1.5;

  div {
    margin: 0;
  }
`;

const NoStreetSpacer = styled.div`
  height: 2px;
`;

const RightContent = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 768px) {
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;

  &:last-child {
    padding-right: 0;
  }
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  text-decoration: none;
  font-size: 1.8rem;
  transition: all 0.2s ease;
  padding: 0 0.5rem;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const PoweredBy = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  color: inherit;
`;

const PoweredByText = styled.div`
  opacity: 0.8;
  font-size: 0.9rem;
  font-family: "Open Sans", sans-serif;
`;

const RecaptchaNotice = styled.div`
  color: ${({ $textColor }) => $textColor};
  opacity: 0.7;
  font-family: "Arial", sans-serif;
  line-height: 1.4;
  margin-top: 0.25rem;
  text-align: center;
  font-size: 0.5rem;
  a {
    &:hover {
      color: #278dc1 !important;
    }
  }
`;

export default FooterSection;
